import i18n from '@/i18n'
import { thingService } from '@/services/thing.service'
import { groupService } from '../../../services/group.service'
import { mapActions, mapGetters } from 'vuex'
import { SnackbarStyle, EventHub } from '@/constants/constants'
import { DialogGroup } from "@/tools/dialog"
import TreeSelectorComponent from '@/components/commons/tree-selector/TreeSelectorComponent.vue'
import { clone } from 'ramda'
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

export default {
    name: 'GroupDialogComponent',
    components: {
        TreeSelectorComponent,
        AutocompleteComponent
    },
    props: {
        // v-model injects the object to be edited into props.value
        model: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        // model del formulario
        validForm: false,
        // reglas de validación
        validationRules: {
            required: (value) => !!value || `${i18n.t('required')}`,
        },
        // cosas
        things: [],
        // errores para el campo del núm de línea
        nameError: [],
        // model para el dialog
        showEditDialog: false,
        // título del dialog
        editDialogTitle: i18n.t('group.editDialog.titleNew'),
        // normalizer para el treeselect
        normalizer: (node) => {
            return {
                id: node._id,
                label: node.name,
                children: node.children
            }
        },
        // empresas
        enterprises: [],
        // cosas seleccionadas
        selectedThingIds: [],
        // nombre actual
        actualName: null
    }),
    computed: {
        /**
         * Breadcrumbs de los dialog
         */
        ...mapGetters('breadcrumbs', [
            'getBread'
        ]),
    },
    created() {
        this.actualName = this.model.name

        /**
         * El modelo de datos del diálogo se carga a través del objeto del breadcrumb
         */
        this.unwatch = this.$store.watch(
            (state, getters) => getters['breadcrumbs/getBread'],
            (newBreads) => {
                const lastBreadcrumb = this.getBread[newBreads.length - 1]
                if (!lastBreadcrumb) {
                    this.cancel()
                } else {
                    if (lastBreadcrumb.key === DialogGroup) {
                        if (lastBreadcrumb.model) {
                            const modelData = this.model
                            Object.assign(modelData, lastBreadcrumb.model)
                        }
                    }
                }
            },
            {deep: true})
    },
    mounted() {
        /**
         * El modelo de datos del diálogo se carga a través del objeto del breadcrumb
         */
        const lastBreadcrumb = this.getBread[this.getBread.length - 1]
        if (!lastBreadcrumb) {
            this.cancel()
        } else {
            if (lastBreadcrumb.key === DialogGroup) {
                if (lastBreadcrumb.model) {
                    const modelData = this.model
                    Object.assign(modelData, lastBreadcrumb.model)
                }
            }
        }
    },
    beforeDestroy() {
        // elimino la instancia del watcher del breadcrumb
        this.unwatch()
    },
    methods: {
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * actions del breadcrumb
         */
        ...mapActions('breadcrumbs', [
            'pullBread',
            'pushBread'
        ]),
        /**
         * Mensaje de error cuando el nombre está duplicado
         */
        nameDuplicatedMethod() {
            if (this.model.name === this.actualName) return
            groupService.validateGroupName(this.model.name).then(response => {
                if (response) {
                    this.nameError.push(`${i18n.t('group.duplicate')}`)
                } else {
                    this.nameError = []
                }
            })
        },
        /**
         * Arreglo de cosas según la empresa
         */
        async getData(enterpriseIds) {
            this.things = await thingService.getThingsWithoutGroupByEnterpriseId(enterpriseIds)
            this.validateAndConcat()
        },
        /**
         * Resetea los campos del formulario de creación/actualización
         */
        resetFields() {
            if (this.$refs.form) {
                this.$refs.form.reset()
            }
        },
        /**
         * Click fuera del dialog
         */
        clickOutside() {
            this.cancel()
        },
        /**
         * Cancelar
         */
        cancel() {
            this.pullBread(DialogGroup)
            this.resetFields()
        },
        /**
         * Guardar
         */
        save() {
            const groupDto = this.getGroupDto(this.model.update)
            if (this.model.update) {
                groupService.update(groupDto, this.selectedThingIds).then(() => {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('group.successUpdateMessage'),
                        timeout: 6000,
                        style: SnackbarStyle.SUCCESS
                    })
                    this.resetFields()
                    this.$emit('commitGroup')
                    this.cancel()
                    this.$eventBus.$emit(EventHub.COMMIT_GROUP, groupDto)
                })
            } else {
                groupService.create(groupDto, this.selectedThingIds).then(insertedId => {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('group.successCreateMessage'),
                        timeout: 6000,
                        style: SnackbarStyle.SUCCESS
                    })
                    this.resetFields()
                    this.$emit('commitGroup')
                    this.cancel()
                    groupDto._id = insertedId
                    this.$eventBus.$emit(EventHub.COMMIT_GROUP, groupDto)
                })
            }
        },
        /**
         * Genera el DTO para actualizar / crear un grupo
         * @param {*} update
         */
        getGroupDto(update = false) {
            const result = {
                name: this.model.name,
                enterpriseId: this.model.enterpriseId
            }
            if (update) {
                result._id = this.model._id
            }
            return result
        },
        /**
         * Se ejecuta con el evento input del vue-treeselect para cambiar el valor del arreglo selectedThingIds
         * @param {*} thingIds
         */
        setSelectedThingIds(thingIds) {
            this.selectedThingIds = thingIds
        },
        /**
         * Para agregar las cosas asignadas al grupo al selector
         */
        validateAndConcat() {
            let thingData
            this.model.things.forEach(thing => {
                thingData = this.things.find(t => t._id === thing._id)
                if (!thingData) {
                    this.things.push(thing)
                }
            })
        },
        /**
         * Cada vez que se modifica la selección de la empresa, se cargan
         */
        enterpriseSelectedFunction (newValue) {
            this.model.enterpriseId = newValue._id ? newValue._id : newValue;
        },
    },
    watch: {
        model: {
            deep: true,
            immediate: true,
            handler(newModel) {
                this.enterprises = clone(newModel.enterprises)
                if (newModel.enterpriseId) {
                    this.selectedThingIds = []
                    this.getData([newModel.enterpriseId])
                } else {
                    this.selectedThingIds = []
                }
                this.selectedThingIds = clone(newModel.thingIds)
                this.validateAndConcat()
            }
        },
        'model.enterpriseId': {
            handler: function(val) {
                if (val) {
                    this.selectedThingIds = []
                    this.getData([val])
                } else {
                    this.selectedThingIds = []
                }
            }
        }
    }
}
